import ColumnFilterContainer from '@/components/ColumnFilters/ColumnFilterContainer';
import { useGetFluxQuery } from '@/features/api/endpoints/fluxEndpoints';
import type { ColumnFilterProps } from '@/models/flowFilter.type';
import { MultiSelect } from '@mantine/core';
import { useTranslate } from '@tolgee/react';
import { useMemo } from 'react';

const FLUX_FIELDS = ['id', 'nom_court'] as const;
// Il n'y a qu'une quinzaine de flux : une seule page suffit, sans pagination.
const FLUX_PAGE_SIZE = 100;

/** Filtre à choix multiples sur les flux ; les valeurs sont leurs noms courts (contenu de `fluxname`). */
const FilterFlux = ({ label, value, onChange }: ColumnFilterProps) => {
  const { t } = useTranslate();
  const { data, isFetching, isError } = useGetFluxQuery({
    page: 1,
    pageSize: FLUX_PAGE_SIZE,
    fields: FLUX_FIELDS,
    orderBy: 'nom_court asc',
  });

  const selected = useMemo(() => (value?.kind === 'select' ? value.values : []), [value]);

  const options = useMemo(() => {
    const names = new Set(
      (data?.rows ?? [])
        .map(row => row.nom_court)
        .filter((nomCourt): nomCourt is string => Boolean(nomCourt)),
    );
    // Un nom venu de l'URL reste affiché même si la liste n'est pas (encore) chargée.
    selected.forEach(nomCourt => names.add(nomCourt));
    return Array.from(names, nomCourt => ({ value: nomCourt, label: nomCourt }));
  }, [data, selected]);

  return (
    <ColumnFilterContainer onReset={() => onChange(undefined)}>
      <MultiSelect
        searchable
        clearable
        // Largeur fixe : sans elle, le champ (et la fenêtre du filtre) s'élargit à chaque flux
        // sélectionné ; les valeurs passent alors à la ligne.
        w={{ base: 240, sm: 320 }}
        maw="100%"
        label={label}
        data={options}
        value={selected}
        disabled={isFetching && options.length === 0}
        error={isError ? t('common.filters.flux.error') : undefined}
        nothingFoundMessage={t('common.filters.flux.empty')}
        comboboxProps={{ withinPortal: false }}
        onChange={values => onChange(values.length > 0 ? { kind: 'select', values } : undefined)}
      />
    </ColumnFilterContainer>
  );
};

export default FilterFlux;
